import { Button, Dropdown, Space, Pagination } from "antd";
import SearchInput from "../components/Forms/SearchInput";
import { useEffect, useState } from "react";
import { postApi, useApiCall } from "../services/api";
import {
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import { formatDate, handleRouteNav } from "../utils";
import Loader from "../common/Loader";
import PrimaryButton from "../components/Forms/PrimaryButton";
import Filters from "../components/Filters";
import CustomBadge from "../components/Charts/CustomBadge";
import {
  modificationTitlesTags,
  perPageOptions,
  showDevLogs,
} from "../constant";
import { useSelector } from "react-redux";
import EmptySection from "../components/Layout/EmptySection";
import {
  PurgeButton,
  PurgeModalUI,
  usePurgeModal,
} from "../components/models/PurgeModal";
import usePagination from "../hooks/usePagination";

const Modification = () => {
  const { appliedFilters, DrawerUI, FilterButton } = Filters({
    filters: [
      {
        type: "select",
        key: "category",
        label: "Category",
        options: [
          {
            label: modificationTitlesTags.pendingTitle,
            value: modificationTitlesTags.pendingTitle,
          },
          {
            label: modificationTitlesTags.updateTitle,
            value: modificationTitlesTags.updateTitle,
          },
        ],
      },
      {
        type: "select",
        key: "status",
        label: "Status",
        options: [
          { label: "Approved", value: "approved" },
          { label: "Rejected", value: "rejected" },
          { label: "Pending", value: "pending" },
        ],
      },
      {
        type: "daterange",
        key: "daterange",
        label: "Date",
      },
    ],
    onChange: () => resetPage(),
  });
  const { loading, postApi } = useApiCall();
  const [data, setData] = useState([]);
  const { pagination, setPagination, resetPage } = usePagination();
  const [searchParams, setSearchParams] = useSearchParams();
  const { is_sales_person, user_id, role_id, is_customer, customer_id } =
    useSelector((state) => state.auth);
  const purgeHook = usePurgeModal();
  const navigate = useNavigate();
  const query = searchParams.get("q") || "";

  const onSearch = (value, _e, info) => console.log(info?.source, value);

  const handleOnSearchChange = (e) => {
    const value = e.target.value;
    setSearchParams(
      (prev) => {
        if (value) prev.set("q", value);
        else prev.delete("q");
        return prev;
      },
      { replace: true },
    );
    resetPage();
  };

  const fetchModificationLists = async () => {
    const { status, category, daterange } = appliedFilters;
    try {
      const filters = {
        status: status,
        category: category,
        date_from: daterange?.[0]
          ? formatDate(new Date(daterange[0]), { format: "YYYY-MM-DD" })
          : undefined,
        date_to: daterange?.[1]
          ? formatDate(new Date(daterange[1]), { format: "YYYY-MM-DD" })
          : undefined,
      };

      const res = await postApi("sales/modification-request-list", {
        search: query,
        // page,
        // per_page: perPage,
        user_id: is_sales_person ? user_id : undefined,
        ...(Object.keys(appliedFilters).length ? filters : {}),
      });

      const sortedData =
        res.data?.sort((record, record2) => {
          const combined = [
            ...(record.customer_data || []),
            ...(record.addresses || []),
          ];

          const latest =
            combined.length > 0
              ? combined.sort(
                  (a, b) => new Date(b.updated_at) - new Date(a.updated_at),
                )[0]
              : null;

          const combined2 = [
            ...(record2.customer_data || []),
            ...(record2.addresses || []),
          ];

          const latest2 =
            combined2.length > 0
              ? combined2.sort(
                  (a, b) => new Date(b.updated_at) - new Date(a.updated_at),
                )[0]
              : null;

          return new Date(latest2.updated_at) - new Date(latest.updated_at);
        }) || [];

      showDevLogs && console.table(sortedData);

      const rows = sortedData || [];
      setData(rows);
      setPagination({ total: rows.length });
    } catch (error) {
      console.error("Error fetching modification lists:", error);
    }
  };

  const handlePurge = async (payload) => {
    const finalPayload = {
      ...payload,
      user_type: is_customer ? "Customer" : "User",
      user_id: is_customer ? customer_id : undefined,
    };
    const response = await postApi(
      "sales/address/delete-modification-bulk",
      finalPayload,
      true,
    );

    if (response && response.status) {
      fetchModificationLists();
    }

    return response.status || false;
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchModificationLists();
    }, 300);

    return () => clearTimeout(handler);
  }, [query, appliedFilters]);

  const handleRedirect = (
    customer_id,
    customer_request_id,
    status,
    buttonVisible,
  ) => {
    navigate(handleRouteNav("/modification-profile", role_id), {
      state: {
        user_id: customer_id,
        customer_request_id,
        routeType: "client",
        modificationStatus: status,
        buttonVisible: "compInfo",
        from_modification: true,
      },
    });
  };

  const { setTitle, setDescription, setBackButton } = useOutletContext();

  useEffect(() => {
    setTitle("Modifications");
    setDescription(`${pagination.total} total modifications`);
    setBackButton(false);
  }, [setTitle, setDescription, setBackButton, pagination.total]);

  return (
    <>
      <DrawerUI />
      <>
        <div className="flex flex-col pb-10">
          <div
            className="bg-[#FFFFFF] mb-6"
            style={{ borderRadius: "32px", overflow: "hidden" }}
          >
            <div className="flex justify-between gap-7 items-center px-8 py-6">
              <SearchInput
                onSearch={onSearch}
                value={query}
                onChange={handleOnSearchChange}
                placeholder="Search for modifications"
              />
              <div className="flex gap-3 items-center">
                <FilterButton />
              </div>
            </div>
          </div>

          {/* Loader Wrapper */}
          {loading ? (
            <Loader fullScreen={false} /> // ✅ Use custom Loader
          ) : (
            <>
              {data && data.length > 0 ? (
                data
                  .slice(
                    (pagination.current - 1) * pagination.pageSize,
                    pagination.current * pagination.pageSize,
                  )
                  .map((record, i) => {
                    const combined = [
                      ...(record.customer_data || []),
                      ...(record.addresses || []),
                    ];

                    const latest =
                      combined.length > 0
                        ? combined.sort(
                            (a, b) =>
                              new Date(b.updated_at) - new Date(a.updated_at),
                          )[0]
                        : null;

                    const status = latest?.status;
                    const updatedAt = latest?.updated_at;
                    const customerId = latest?.customer_id;

                    let cardTitle = "-";
                    let latestTag = null;

                    if (latest) {
                      if (latest.update_address_id) {
                        // Address record
                        cardTitle = latest.request_title || "-";
                        latestTag = latest.tags || null;
                      } else if (latest.customer_update_id) {
                        // Customer record
                        cardTitle = latest.title || "-";
                        latestTag = latest.tags || null;
                      }
                    }

                    return (
                      <div
                        key={i}
                        className="bg-[#ffffff] p-6 rounded-3xl border border-[#D9D9D9] mb-6"
                      >
                        {/* Header */}
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-5 mb-4 pr-[35px]">
                          <div className="flex flex-col justify-start">
                            <h3 className="text-xl font-semibold text-[#000]">
                              {cardTitle}
                            </h3>
                            <div className="w-full flex justify-start">
                              <p className="text-sm text-gray-600">
                                Updated on -{" "}
                                {formatDate(updatedAt, {
                                  format: "DD/MM/YYYY - h:mma",
                                })}
                              </p>
                            </div>
                          </div>

                          {/* Status + Button */}
                          <div className="flex items-center gap-x-4">
                            {status === "pending" ? (
                              <CustomBadge variant="pending" text="Pending" />
                            ) : status === "rejected" ? (
                              <CustomBadge variant="danger" text="Rejected" />
                            ) : (
                              <CustomBadge variant="success" text="Approved" />
                            )}

                            <PrimaryButton
                              onClick={() =>
                                handleRedirect(
                                  customerId,
                                  record?.customer_request_id,
                                  status,
                                )
                              }
                              text="View Request"
                            />
                          </div>
                        </div>

                        {/* Show only latest tag */}
                        <div className="w-full flex justify-start">
                          {latestTag && (
                            <p className="text-sm text-gray-600">{latestTag}</p>
                          )}
                        </div>
                      </div>
                    );
                  })
              ) : (
                <EmptySection noDataText={"Data"} />
              )}

              {data && data.length > 0 && (
                <>
                  <div className="flex justify-end mt-6 pr-6">
                    <Pagination
                      current={pagination.current}
                      showSizeChanger={true}
                      pageSizeOptions={perPageOptions}
                      pageSize={pagination.pageSize}
                      showTotal={(total, range) =>
                        `Showing ${range[0]}-${range[1]} of ${total} items`
                      }
                      onChange={(newPage, newPageSize) => {
                        console.log(
                          "newPage, newPageSize",
                          newPage,
                          newPageSize,
                        );
                        setPagination((prev) => ({
                          current: newPageSize !== prev.pageSize ? 1 : newPage,
                          pageSize: newPageSize,
                        }));
                      }}
                      total={pagination.total}
                    />
                  </div>

                  {!is_sales_person && (
                    <div className="flex justify-end mt-4">
                      <PurgeButton
                        hookData={purgeHook}
                        buttonLabel="Purge Modifications"
                      />
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </>
      <PurgeModalUI
        hookData={purgeHook}
        onConfirm={handlePurge}
        loading={loading}
        title="Purge Modifications"
      />
    </>
  );
};

export default Modification;
